import { I18n } from '@calendly/i18n'
import { Avatar } from '@calendly/ui/components/avatar'
import { BodyText, HeadingText } from '@calendly/ui/components/typography'
import { useDeviceType } from '@calendly/ui/hooks/use-device-type'
import { colorTextSecondary } from '@calendly/ui/theme'
import { useDispatch } from 'react-redux'

import { updateAvailabilityFormView } from 'actions/availability/form'
import { useGetUserQuery } from 'common'
import { Layout } from 'components/layout'

import { ActiveOn } from './active_on'
import { CloseControl } from './close_control'
import { ScheduleSelect } from './schedule_select'
import { Settings } from './settings'
import { ViewSwitcher } from './view_switcher'
import { useGetAvailabilitySchedulesQuery } from '../../api'

const HeaderContainer = (props) => <Layout direction="column" gap="8" {...props} />
const HeaderWrapper = (props) => <Layout direction="column" {...props} />
const SubheaderWrapper = (props) => <Layout justify="space-between" align="baseline" gap="16" {...props} />
const ScheduleWrapper = (props) => <Layout justify="space-between" align="center" gap="16" {...props} />
const ActionsWrapper = (props) => <Layout noShrink align="center" gap="8" {...props} />
const Eyebrow = (props) => <HeadingText as="p" level={3} variant="eyebrow" color={colorTextSecondary} {...props} />

const CurrentUserData = () => {
  const { data: { name, avatar_letter: avatarLetter, avatar_url: avatarUrl } } = useGetUserQuery()

  return (
    <Layout gap="8" align="center">
      <Avatar
        aria-label={name}
        letter={avatarLetter}
        src={avatarUrl}
        size="x-small"
      />
      <BodyText weight="bold" size="medium" color="muted">{name}</BodyText>
    </Layout>
  )
}

export const Header = ({ isFormDirty, selectedScheduleId, currentEventType, trackLocation, onClose }) => {
  const dispatch = useDispatch()
  const isDesktopUp = useDeviceType('isDesktopUp')

  const { data: { availability_form_view: formView, primary_availability_rule_id: defaultScheduleId } } = useGetUserQuery()
  const { data } = useGetAvailabilitySchedulesQuery()

  const allSchedules = data || []
  const selectedSchedule = allSchedules.find(({ id: scheduleId }) => scheduleId === selectedScheduleId)

  const selectedScheduleName = selectedScheduleId === defaultScheduleId
    ? `${selectedSchedule.name} ${I18n.t('publisher.event_type_editor.sections.available_hours.default_indicator')}`
    : selectedSchedule.name

  const sortedSchedules = allSchedules.slice().sort(({ id: scheduleId }) => (scheduleId === defaultScheduleId ? -1 : 1))

  const handleViewSwitch = (selectedView) => {
    if (selectedView === formView) return

    dispatch(updateAvailabilityFormView(selectedView))
  }

  return (
    <HeaderContainer>
      <HeaderWrapper gap={onClose ? '8' : '0'}>
        {onClose ? (
          <SubheaderWrapper>
            <CurrentUserData />
            <CloseControl
              selectedScheduleId={selectedScheduleId}
              onClose={onClose}
            />
          </SubheaderWrapper>
        ) : (
          <Eyebrow id="rule-select-label">
            {I18n.t('publisher.availability.schedules.schedule')}
          </Eyebrow>
        )}
        <ScheduleWrapper>
          {!onClose ? (
            <ScheduleSelect
              defaultScheduleId={defaultScheduleId}
              disabled={isFormDirty}
              selectedScheduleId={selectedScheduleId}
              sortedSchedules={sortedSchedules}
            />
          ) : (
            <HeadingText level={2}>{selectedScheduleName}</HeadingText>
          )}
          <ActionsWrapper>
            {isDesktopUp && (
              <ViewSwitcher
                view={formView}
                onChange={handleViewSwitch}
                trackLocation={trackLocation}
              />
            )}
            {!onClose && <Settings selectedScheduleId={selectedScheduleId} />}
          </ActionsWrapper>
        </ScheduleWrapper>
      </HeaderWrapper>
      <ActiveOn
        selectedScheduleId={selectedScheduleId}
        currentEventType={currentEventType}
        trackLocation={trackLocation}
        variant={onClose ? 'banner' : 'inline'}
      />
    </HeaderContainer>
  )
}
